import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material';
import * as XlsxPopulate from 'xlsx-populate/browser/xlsx-populate';
import { saveAs } from 'file-saver';

import { UiService } from '../../../services/ui.service';
import { DataSourceService } from '../../../services/data-source.service';
import { InfoDialogComponent } from '../../../dialogs/info-dialog/info-dialog.component';
import { Helpers } from '../../../models/helpers';
import { CancelConfirmDialogComponent } from '../../../dialogs/cancel-confirm-dialog/cancel-confirm-dialog.component';


interface LoadedEmployees {
  employeeNumber: number,
  index: number
};

@Component({
  selector: 'app-veab-registration',
  templateUrl: './veab-registration.component.html',
  styleUrls: ['./veab-registration.component.scss']
})
export class VeabRegistrationComponent implements OnInit {

  public employeeFile: Blob = null;
  public action = 'add';
  public template = '/assets/img/plantilla.png';
  public loadedEmployees: Array<LoadedEmployees> = [];
  public displayedColumns = ['row', 'employeeNumber'];
  public actionDescriptions = {
    add: "Los colaboradores incluidos en esta lista se darán de alta al bono VEAB",
    remove: "Los colaboradores incluidos en esta lista serán dados de baja del bono VEAB"
  };

  constructor(
    private _UIService: UiService,
    private _dataSource: DataSourceService,
    private _dialog: MatDialog,
  ) { }

  ngOnInit() {
  }

  public onSelectAction(action: string): void {
    this.action = action;
  }

  public async createTemplateFile(): Promise<void> {

    const xlsx = await XlsxPopulate.fromBlankAsync();
    xlsx.sheet(0).name('Listado');
    await this._createXlsxHeader(xlsx);

    try {
      saveAs( await xlsx.outputAsync('blob'), 'akiba-VEAB.xlsx' );
    } catch (error) {
      this._showInfoMessage(`Ocurrió un error al generar la plantilla<br>Vuelva a intentarlo más tarde`);
    }

  }

  public onFileChange(event: Event): void {
    this.employeeFile = (event.target as HTMLInputElement).files[0];

    const reader = new FileReader();
    reader.onload = async (e) => {

      this._UIService.showSpinner();

      const workBook = await XlsxPopulate.fromDataAsync(reader.result);
      const rows = workBook.sheet(0).usedRange() ? workBook.sheet(0).usedRange().value() as Array<any> : [];

      rows.shift();
      this.loadedEmployees = rows.filter(r => r[0] !== undefined).map((r, index) => ({ employeeNumber: r[0], index }));

      if ( !this.loadedEmployees.length ) {
        this.employeeFile = null;
        this._showInfoMessage(`El archivo no contiene datos`);
      }

      this._UIService.stopSpinner();

    };

    reader.readAsArrayBuffer(this.employeeFile);

  }

  public async  executeAction() {
    if ( !this.action ) {
      this._showInfoMessage(`Se produjo un error al obtener la accción seleccionada`);
      return;
    }

    const employeeNumbers = this.loadedEmployees.map(le => le.employeeNumber);

    let employeeNumberChunks = this._generateChunks( employeeNumbers );

    const confirmation = await this._confirmAction().afterClosed().toPromise();

    if ( confirmation ) {
      this._UIService.showSpinner();

      if ( this.action === 'add' ) 
        await this._addLoadedEmployeesToVEABBonus( employeeNumberChunks );
      else
        await this._removeLoadedEmployeesToVEABBonus( employeeNumberChunks );

      this._UIService.stopSpinner();

      this._processDone();
    }
    
  }

  public cancel(): void {
    this.employeeFile = null;
    this.action = 'add';
    this.loadedEmployees = [];
  }

  public async downloadReport() {
    this._UIService.showSpinner();
    const xlsx = await XlsxPopulate.fromBlankAsync();
    xlsx.sheet(0).name('Listado');
    await this._createXlsxHeader(xlsx);
    this._dataSource.callbackWithArray = async (batchResults: Array<any>) => {

      if ( !batchResults.length ) return false;
      let i = 1;

      for (const data of batchResults) {
         
        if (!data) {
          continue;
        }

        const cell = xlsx.sheet(0).row(i + 1 ).cell(1);
        cell.value(data.workInformation.employeeNumber);
        const len = Math.max(20, data.workInformation.employeeNumber.length + 1);
        xlsx.sheet(0).column(i + 1).width(len);
        i++;
      }

      return true;

    };

    try {
      await this._dataSource.loadEmployeesWithActiveVEAB(5000, 15000);
      xlsx.outputAsync('blob').then(out => {
        saveAs(out, 'VEAB_activos-akiba.xlsx');
        this._UIService.stopSpinner();
      }).catch((error: any) => {
        this._UIService.stopSpinner();
        this._showInfoMessage('Ocurrió un error al generar el reporte.<br>Intente más tarde');
      });
    } catch (error) {
      this._showInfoMessage('Ocurrió un error al obtener los datos.<br>Intente más tarde');
      this._UIService.stopSpinner();
    }
  }

  private async _addLoadedEmployeesToVEABBonus(employeeNumberChunks: Array<Array<number>>) {

    for (const chunk of employeeNumberChunks) {
      try {
        await this._dataSource.addEmployeesToVEABBonus( chunk );
      } catch (error) {
        this._showInfoMessage(`Ocurrió un error al dar de alta los usuarios`);
        this.cancel();
      }
    }

  }

  private async _removeLoadedEmployeesToVEABBonus(employeeNumberChunks: Array<Array<number>>) {

    for (const chunk of employeeNumberChunks) {
      try {
        await this._dataSource.removeEmployeesToVEABBonus( chunk );
        await this._dataSource.setEmployeeVEABBonusToZero( chunk );
      } catch (error) {
        this._showInfoMessage(`Ocurrió un error al dar de baja los usuarios`);
        this.cancel();
      }
    }

  }

  private _confirmAction() {
    const spanishAction = this.action === 'add' ? 'alta' : 'baja'; 
    return this._showConfirmationDialog(`¿Desea continuar para dar de ${ spanishAction } a los colaboradores ingresados?`);
  }

  private _processDone() {
    this._showInfoMessage(`El proceso terminó con éxito`);
    this.cancel();
  }

  private _generateChunks( employeeNumbers: number[] ): Array<Array<number>> {
    if ( employeeNumbers.length > 499 )
      return  Helpers.chunk( employeeNumbers, 499 );
    else
    return [ employeeNumbers ]
  }


  private async _createXlsxHeader(xlsx: any) {
    const sheetData = ['Número de Colaborador'];
    const sheetEntries = sheetData.entries();

    for (const [i, v] of sheetEntries) {
      const cell = xlsx.sheet(0).row(1).cell(i + 1);
      cell.value(v);
      cell.style('shrinkToFit', false);
      cell.style('bold', true);
      cell.style('fill', '1565c0');
      cell.style('fontColor', 'ffffff');
      const len = Math.max(20, v.length + 1);
      xlsx.sheet(0).column(i + 1).width(len);
    }
  }

  private _showInfoMessage(message: string, buttonText: string = 'Aceptar'): void {
    this._dialog.open(InfoDialogComponent, {
      data: { message, close: buttonText }
    });
  }

  private _showConfirmationDialog(message: string) {
    return this._dialog.open(CancelConfirmDialogComponent, {
      data: {
        message: message,
        cancelButton: 'Cancelar',
        confirmButton: 'Confirmar'
      }
    });
  }

}
